/**
 * 슈퍼관리자 대시보드 추이 그래프 — 서버 추이 데이터만 그린다.
 *
 * 🔴 예전에는 서버 추이가 없으면 화면이 현재 값에 임의 배율(0.82·0.87… 등)을 곱해 가짜 추이를 만들어 그렸다.
 *    지금은 만들지 않는다. 데이터가 없으면 빈 상태, 없는 점(null)은 0 으로 채우지 않고 선을 끊는다.
 *    서버가 숫자를 준 점은 예전과 같은 좌표·같은 선으로 그린다.
 */

export type TrendPoint = number | null;

/** 서버 배열을 라벨 길이에 맞춘 점 목록으로. 숫자가 아닌 값·빠진 자리는 null(없는 점) */
export const toTrendPoints = (data: unknown, length: number): TrendPoint[] => {
    const arr = Array.isArray(data) ? data : [];
    return Array.from({ length }, (_, i) => {
        const v = arr[i];
        return typeof v === 'number' && Number.isFinite(v) ? v : null;
    });
};

/** 축 범위 계산용 — 있는 점만 */
export const presentValues = (points: readonly TrendPoint[]): number[] =>
    points.filter((v): v is number => typeof v === 'number');

export const hasAnyPoint = (points: readonly TrendPoint[]): boolean => points.some((v) => typeof v === 'number');

/**
 * 선 경로. 없는 점에서 끊고 다음 있는 점에서 새로 시작한다.
 * 빈 점이 없으면 예전 식(`i === 0 ? 'M' : 'L'`)과 같은 문자열이 나온다.
 */
export const buildLinePath = (
    points: readonly TrendPoint[],
    getX: (i: number) => number,
    getY: (v: number) => number
): string => {
    const parts: string[] = [];
    let penDown = false;
    points.forEach((v, i) => {
        if (typeof v !== 'number') {
            penDown = false;
            return;
        }
        parts.push(`${penDown ? 'L' : 'M'}${getX(i)},${getY(v)}`);
        penDown = true;
    });
    return parts.join(' ');
};

/** 마지막으로 있는 점(오른쪽 끝 이름표 자리). 없으면 null */
export const lastPresentPoint = (points: readonly TrendPoint[]): { index: number; value: number } | null => {
    for (let i = points.length - 1; i >= 0; i--) {
        const v = points[i];
        if (typeof v === 'number') return { index: i, value: v };
    }
    return null;
};

/** 툴팁 정렬 — 값 내림차순, 없는 점은 맨 뒤(원래 순서 유지) */
export const compareTrendValueDesc = (a: TrendPoint, b: TrendPoint): number => {
    if (a === null && b === null) return 0;
    if (a === null) return 1;
    if (b === null) return -1;
    return b - a;
};
